import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import "./appointment.scss";
import { HMS_IFRAME_MESSAGES } from "./iframeMessages";
import { patientid } from "../../Redux/Slice/rootSlice";

const APPOINTMENT_URL =
  process.env.REACT_APP_APPOINTMENT_URL ||
  (process.env.NODE_ENV === "development" ? "http://localhost:5173/appointment" : "/appointment-module");
const APPOINTMENT_ORIGIN = APPOINTMENT_URL.startsWith("http")
  ? new URL(APPOINTMENT_URL).origin
  : window.location.origin;

function parseStoredUser(userdata) {
  if (!userdata) return null;
  try {
    return JSON.parse(userdata);
  } catch (e) {
    return null;
  }
}

function Appointment() {
  const iframeRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const sideactive = useSelector((state) => state.counter.sidebarnav);
  const [isLoading, setIsLoading] = useState(true);

  const appointmentPath = useMemo(() => {
    return location.pathname.replace("/appointment", "") || "/";
  }, [location.pathname]);

  const iframeSrc = `${APPOINTMENT_URL.replace(/\/$/, "")}${appointmentPath}${location.search}`;

  useEffect(() => {
    setIsLoading(true);
  }, [iframeSrc]);

  const sendAuthToIframe = () => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) return false;

    const token = localStorage.getItem("token");
    const userdata = localStorage.getItem("userdata");
    const role = localStorage.getItem("role") || parseStoredUser(userdata)?.role || "";
    const branch = localStorage.getItem("selectedBranch") || localStorage.getItem("branch");
    if (!token) return false;

    try {
      iframe.contentWindow.postMessage(
        {
          type: HMS_IFRAME_MESSAGES.authToken,
          token,
          user: parseStoredUser(userdata),
          role,
          branch,
        },
        APPOINTMENT_ORIGIN
      );
      return true;
    } catch (e) {
      return false;
    }
  };

  useEffect(() => {
    const handleMessage = (event) => {
      if (
        event.origin === APPOINTMENT_ORIGIN &&
        event.source === iframeRef.current?.contentWindow
      ) {
        if (event.data?.type === HMS_IFRAME_MESSAGES.ready) {
          sendAuthToIframe();
          setIsLoading(false);
        } else if (event.data?.type === HMS_IFRAME_MESSAGES.authRequest) {
          sendAuthToIframe();
        } else if (event.data?.type === HMS_IFRAME_MESSAGES.navigate && event.data?.path) {
          if (event.data?.patientId) {
            dispatch(patientid(event.data.patientId));
            localStorage.setItem("patientid", event.data.patientId);
          }
          navigate(event.data.path);
        } else if (event.data?.type === "HMS_UNAUTHORIZED") {
          localStorage.clear();
          window.location.href = "/login";
        }
      }
    };

    window.addEventListener("message", handleMessage);

    // Fallback: stop loading after 2.5s if iframe already initialized
    const fallbackTimer = setTimeout(() => {
      sendAuthToIframe();
      setIsLoading(false);
    }, 2500);

    return () => {
      window.removeEventListener("message", handleMessage);
      clearTimeout(fallbackTimer);
    };
  }, [navigate]);

  return (
    <div className={`appointment-container ${sideactive ? "sidebar-open" : "sidebar-closed"}`}>
      {isLoading && (
        <div className="appointment-loader" role="status" aria-live="polite">
          <div className="appointment-loader-card">
            <span className="appointment-loader-spinner" aria-hidden="true" />
            <strong>Loading Appointment Management...</strong>
            <span>Preparing appointment schedules and doctor calendar.</span>
          </div>
        </div>
      )}
      <iframe
        ref={iframeRef}
        src={iframeSrc}
        title="Appointment Module"
        className="appointment-iframe"
      />
    </div>
  );
}

export default Appointment;
